import React from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { Box, Checkbox, FormControlLabel, Button, FormGroup } from '@mui/material'
import { CAMPAIGN_ERROR_MESSAGE } from '~/constants/campaign'
import { z } from 'zod'
import { PrTargetAge as PrTargetAgeEnum } from '~/generated/graphql'
import { ValidationErrorMessage } from '~/components/common/ValidationErrorMessage'

export const prTargetAgeSchemaFields = {
  prTargetAge: z
    .array(
      z.enum([
        PrTargetAgeEnum.Age_17AndUnder,
        PrTargetAgeEnum.Age_18To_24,
        PrTargetAgeEnum.Age_25To_34,
        PrTargetAgeEnum.Age_35To_44,
        PrTargetAgeEnum.Age_45To_54,
        PrTargetAgeEnum.Age_55AndAbove,
      ]),
    )
    .min(1, CAMPAIGN_ERROR_MESSAGE.REQUIRED)
    .transform((value) => {
      return Object.values(ageGroupEnumMap).filter((e) => value.includes(e))
    })
    .readonly(),
}
export const prTargetAgeSchema = z.object(prTargetAgeSchemaFields)

export const PrTargetAge = () => {
  const { control } = useFormContext<z.infer<typeof prTargetAgeSchema>>()

  return (
    <Controller
      name="prTargetAge"
      control={control}
      render={({ field: { ref, ...field }, fieldState: { error } }) => {
        const isAllChecked = field.value.length === ageGroups.length

        const handleToggleAll = () => {
          const newValue = isAllChecked ? [] : Object.values(ageGroupEnumMap)
          field.onChange(newValue)
        }

        const handleChange = (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
          const selected = Object.values(ageGroupEnumMap)[index]
          const updated = event.target.checked ? [...field.value, selected] : field.value.filter((v) => v !== selected)
          field.onChange(updated)
        }

        return (
          <Box mt={1}>
            <Button variant="text" onClick={handleToggleAll}>
              {isAllChecked ? 'すべて解除' : 'すべて選択'}
            </Button>
            <FormGroup row>
              <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
                {ageGroups.map((label, index) => (
                  <FormControlLabel
                    key={label}
                    label={label}
                    control={
                      <Checkbox
                        {...field}
                        checked={field.value.includes(Object.values(ageGroupEnumMap)[index] as PrTargetAgeEnum)}
                        onChange={handleChange(index)}
                        inputRef={index === 0 ? ref : undefined}
                      />
                    }
                    sx={{ width: 'fit-content' }}
                  />
                ))}
                <ValidationErrorMessage error={error?.message} />
              </Box>
            </FormGroup>
          </Box>
        )
      }}
    />
  )
}

const ageGroups = ['17歳以下', '18〜24歳', '25〜34歳', '35〜44歳', '45〜54歳', '55歳以上']

const ageGroupEnumMap = {
  '17歳以下': PrTargetAgeEnum.Age_17AndUnder,
  '18〜24歳': PrTargetAgeEnum.Age_18To_24,
  '25〜34歳': PrTargetAgeEnum.Age_25To_34,
  '35〜44歳': PrTargetAgeEnum.Age_35To_44,
  '45〜54歳': PrTargetAgeEnum.Age_45To_54,
  '55歳以上': PrTargetAgeEnum.Age_55AndAbove,
}
